/**
 * physicsStep.ts
 * Pure physics execution for one fixed timestep — no Reanimated dependency.
 * Shared by native (useFrameCallback) and web (RAF) game loops.
 * NO React imports, NO Reanimated imports, NO async.
 * @module physicsStep
 */

import {
  GRAVITY,
  MAX_FALL_SPEED,
  STOMP_BOUNCE,
} from './physics';
import { gameState } from './gameState';
import { resolveAllPlatforms, GROUND_SKIN } from './collision';
import { resolveOneWayPlatforms, hasMinimumGroundSupport } from './oneWayCollision';
import { updateCamera } from './camera';
import { inputState, clearRisingEdgeFlags } from '../input/inputState';
import { updateHudSnapshot } from './hudSnapshot';
import { COYOTE_FRAMES, NOVA_WIDTH, NOVA_HEIGHT, CROUCH_MAX_SPEED } from '../../constants/gameConstants';
import { applyHorizontalInput, applyJump } from './novaLocomotion';
import { crouchTopOffset, resolveCrouchState } from './novaCrouch';
import { applyWallSlide } from './wallSlide';
import { isDashing, stepDash } from './novaDash';
import { deathRespawn } from './respawn';
import {
  updateAttackCooldown,
  tryFireProjectiles,
  updateProjectiles,
  runCombatStep,
} from './projectileSystem';
import { stepCollectibles } from './collectibleStep';
import { stepWeaponPickups } from './weaponPickupStep';
import { updateParticles } from './particleSystem';
import { resolveAnimationState } from './animationState';
import { tickHitEffects } from './combatStep';
import { finaleState } from '../scenes/FinaleSequence';
import { portalX } from '../scenes/finaleState';
import { dispatch } from './gameEventDispatch';
import { applyLevelExitRules } from './bossArena';

let _sceneUpdateHook: ((dt: number) => void) | null = null;
/** Register a per-tick scene callback for checkpoint checks. */
export function setSceneUpdateHook(fn: ((dt: number) => void) | null): void {
  _sceneUpdateHook = fn;
}

/** Sync current gameState values to the HUD snapshot. */
function syncHudSnapshot(): void {
  const { nova, seeds, shardsCollected, shardsTotal, score, lives, elapsedSeconds, projectileKind, ammo } = gameState;
  updateHudSnapshot(nova.health, nova.maxHealth, seeds, shardsCollected, shardsTotal, score, lives, elapsedSeconds, projectileKind, ammo);
}

/**
 * Execute a single fixed-step physics update for Nova.
 * Reads from `gameState` and `inputState`, writes to `gameState`. No React, no async.
 * @param dt - The fixed physics timestep in seconds (always FIXED_STEP).
 */
export function stepPhysics(dt: number): void {
  // Short-circuit when the level is done or the player has lost all lives.
  if (gameState.levelComplete || gameState.gameOver) return;

  // DECISION: When finaleActive, skip normal player physics but still run the
  // scene hook and sync HUD/input. The finale controls Nova's position directly.
  if (gameState.finaleActive) {
    gameState.elapsedSeconds += dt; _sceneUpdateHook?.(dt);
    clearRisingEdgeFlags(); syncHudSnapshot();
    return;
  }

  const nova = gameState.nova;
  const { platforms, width: levelWidth, height: levelHeight } = gameState.level;
  gameState.elapsedSeconds += dt;

  // ── 1. Horizontal input ──────────────────────────────────────────────────
  const locomotionInput = {
    left: inputState.left,
    right: inputState.right,
    jumpPressed: inputState.jumpPressed,
    jumpHeld: inputState.jumpHeld,
  };
  applyHorizontalInput(nova, locomotionInput, dt);

  // ── 2-5. Jump buffer, availability and execution ─────────────────────────
  // Stomp detection lives in combatStep.ts (Nova-vs-enemy overlap + wasFalling).
  applyJump(nova, locomotionInput);

  // ── 5b. Dash ─────────────────────────────────────────────────────────────
  // Runs before gravity: an active dash pins vy, and applying gravity after
  // would curve a move that is meant to be flat.
  stepDash(nova, inputState.dashPressed);
  const dashing = isDashing(nova);

  // ── 6. Gravity ───────────────────────────────────────────────────────────
  if (!dashing) {
    nova.vy += GRAVITY * dt;
    nova.vy = Math.min(nova.vy, MAX_FALL_SPEED);
  }

  // ── 6b. Wall slide ───────────────────────────────────────────────────────
  // After gravity so it clamps the speed Nova would otherwise have reached,
  // and before integration so the clamp applies on the same tick.
  const crouchOffset = crouchTopOffset(nova.isCrouching === true);
  if (!dashing) {
    applyWallSlide(
      nova, platforms, inputState.left, inputState.right,
      nova.y + crouchOffset, NOVA_HEIGHT - crouchOffset,
    );
  } else {
    nova.isWallSliding = false;
  }

  // ── 7. Integrate position ────────────────────────────────────────────────
  nova.x += nova.vx * dt;
  nova.y += nova.vy * dt;

  // DECISION: Capture falling state BEFORE collision so stomp detection works.
  const wasFalling = !nova.isGrounded && nova.vy > 0;

  // DECISION: Previous bottom-Y for one-way pass-through check.
  const prevBottomY = nova.y + NOVA_HEIGHT - nova.vy * dt;

  // ── 8. Collision resolution ──────────────────────────────────────────────
  // DECISION: while crouched the collider shrinks from the top down, so the
  // resolver runs in collider space (top edge = nova.y + crouchOffset) and the
  // result is mapped back out. Feet are invariant under that transform —
  // colliderTop + colliderHeight === nova.y + NOVA_HEIGHT — so prevBottomY,
  // ground support and every other feet-based check below are unaffected.
  const colliderY = nova.y + crouchOffset;
  const colliderH = NOVA_HEIGHT - crouchOffset;
  const hitbox = { x: nova.x, y: colliderY, width: NOVA_WIDTH, height: colliderH };
  const result = resolveAllPlatforms(nova.x, colliderY, hitbox, platforms, nova.vy);

  nova.x = result.x;
  nova.y = result.y - crouchOffset;

  // ── 8b. One-way platform resolution (after solid so grounded Nova isn't interfered)
  const oneWayPlatforms = gameState.level.oneWayPlatforms ?? [];
  if (oneWayPlatforms.length > 0) {
    const owResult = resolveOneWayPlatforms(
      nova.x, nova.y + crouchOffset, hitbox, oneWayPlatforms, nova.vy, prevBottomY,
    );
    result.x = owResult.x;
    result.y = owResult.y;
    nova.x = owResult.x;
    nova.y = owResult.y - crouchOffset;
    if (owResult.landedOnTop) {
      result.landedOnTop = true;
    }
  }

  // ── 8c. Minimum ground support — 40% overlap required to count as grounded.
  if (result.landedOnTop) {
    const owp = gameState.level.oneWayPlatforms ?? [];
    if (!hasMinimumGroundSupport(nova.x, nova.y + NOVA_HEIGHT, NOVA_WIDTH, platforms, owp)) {
      // DECISION: Nudge past GROUND_SKIN so the next tick's collision doesn't
      // trigger forceVerticalLanding (which would snap Nova back to the surface,
      // creating an infinite jitter loop where she appears stuck in mid-air).
      result.landedOnTop = false;
      nova.y += GROUND_SKIN + 1;
      result.y = nova.y + crouchOffset;
    }
  }

  if (result.landedOnTop) {

    if (nova.isStomping) {
      // DECISION: Stomp bounce replaces vy with upward impulse.
      nova.vy = STOMP_BOUNCE;
      nova.isStomping = false;
    } else {
      nova.vy = 0;
    }
    nova.isGrounded = true;
    nova.coyoteFrames = COYOTE_FRAMES;
    // DECISION: SET (not +=) so rapid pogo lands can't stack banked jumps.
    if (nova.canDoubleJump === true) nova.airJumpsRemaining = 1;
  } else {
    if (nova.isGrounded) {
      nova.isGrounded = false;
    }
    if (nova.coyoteFrames > 0) {
      nova.coyoteFrames--;
    }
  }

  if (result.hitCeiling) {
    nova.vy = Math.max(0, nova.vy);
    nova.isJumping = false;
  }

  // DECISION: Compute crouch AFTER collision — isGrounded is now finalised
  // for this frame, so the speed cap reflects actual ground contact without
  // a 1-tick lag when jumping or landing while holding down. The collider above
  // uses the flag resolved on the previous tick; one tick of delay on the
  // shrink is imperceptible, and resolving here guarantees the headroom test
  // has already run before the box is ever allowed to grow back.
  resolveCrouchState(nova, platforms, inputState.down && nova.isGrounded);
  const isCrouching = nova.isCrouching === true;
  // The dash owns vx outright, so the crouch cap must not fight it — otherwise
  // dashing while ducked would clamp a 560 px/s move down to 60 px/s.
  if (isCrouching && !dashing) {
    nova.vx = Math.max(-CROUCH_MAX_SPEED, Math.min(CROUCH_MAX_SPEED, nova.vx));
  }

  if (result.hitLeft || result.hitRight) {
    nova.vx = 0;
  }

  // ── 9. Level bounds + death plane + exit trigger ─────────────────────────
  nova.x = Math.max(0, nova.x);

  // Portal wall — during APPROACH beat, the portal archway blocks Nova.
  // DECISION: Wall is at the portal's left edge (not the gate column) so Nova
  // stops at the visible door sprite. portalRightX is not needed — we want
  // the wall at the portal's leading (left) face.
  if (finaleState.beat === 'APPROACH' && finaleState.gateX > 0) {
    const wallX = portalX();
    if (nova.x + NOVA_WIDTH > wallX) {
      nova.x = wallX - NOVA_WIDTH;
      nova.vx = 0;
    }
  }

  // Pre-combat exit gate — see bossArena.applyLevelExitRules.
  if (applyLevelExitRules(nova, levelWidth, finaleState.beat !== 'NONE')) {
    syncHudSnapshot(); clearRisingEdgeFlags();
    return;
  }

  if (nova.y > levelHeight) {
    dispatch.death();
    deathRespawn();
    syncHudSnapshot(); clearRisingEdgeFlags();
    return;
  }

  if (nova.health <= 0) {
    dispatch.death();
    deathRespawn();
    syncHudSnapshot(); clearRisingEdgeFlags(); // Sync AFTER respawn — HUD shows restored health
    return;
  }

  // ── 10. Animation state ──────────────────────────────────────────────────
  resolveAnimationState(nova, isCrouching);

  updateAttackCooldown(dt);
  tryFireProjectiles(nova.x, nova.y, nova.isGrounded, nova.facingRight);
  updateProjectiles(dt);
  const invincibilityBeforeCombat = nova.invincibilityFrames;
  runCombatStep(dt, wasFalling);
  tickHitEffects(dt);
  if (invincibilityBeforeCombat > 0 && nova.invincibilityFrames > 0) {
    nova.invincibilityFrames--;
  }
  if (nova.invincibilityFrames === 0) {
    nova.hurtFlash = false;
  }

  if (nova.health <= 0) {
    dispatch.death();
    deathRespawn();
    syncHudSnapshot(); clearRisingEdgeFlags();
    return;
  }

  // Post-combat exit: boss arenas clear via gameState.bossDefeated (combatStep sets it).
  applyLevelExitRules(nova, levelWidth, finaleState.beat !== 'NONE');

  // ── 14. Camera ───────────────────────────────────────────────────────────
  const cam = updateCamera(
    gameState.camera.x, gameState.camera.y,
    nova.x, nova.y, NOVA_WIDTH, NOVA_HEIGHT,
    nova.facingRight, levelWidth, levelHeight,
  );
  gameState.camera.x = cam.x;
  gameState.camera.y = cam.y;

  // ── 15. Pickups (collectibles + weapons) ─────────────────────────────────
  stepCollectibles();
  stepWeaponPickups();
  // ── 16. Particle update ──────────────────────────────────────────────────
  updateParticles(dt);

  _sceneUpdateHook?.(dt);
  clearRisingEdgeFlags();
  syncHudSnapshot();
}
